import { supabase } from "./supabaseClient";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

// `cache: "no-store"` forces a real network request every time.
// Without it, some browsers will serve a cached response from a
// previous session — that hid the gap detail's debug chip until the
// cache happened to expire.

// Attach the current Supabase access token as a bearer header when
// Supabase is configured + the user is signed in. The worker accepts
// requests without a bearer when SUPABASE_JWT_SECRET is unset (dev /
// pre-provisioning), so stub-session mode keeps working unchanged.
async function authHeader(): Promise<Record<string, string>> {
  if (!supabase) return {};
  const { data } = await supabase.auth.getSession();
  if (!data.session) return {};
  return { authorization: `Bearer ${data.session.access_token}` };
}

export async function apiGet<T>(path: string): Promise<T> {
  const auth = await authHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
    headers: { ...auth },
  });
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const auth = await authHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: { "content-type": "application/json", ...auth },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const auth = await authHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PATCH",
    credentials: "include",
    cache: "no-store",
    headers: { "content-type": "application/json", ...auth },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`PATCH ${path} failed: ${res.status} ${text.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

export async function apiDelete<T>(path: string): Promise<T> {
  const auth = await authHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    credentials: "include",
    cache: "no-store",
    headers: { ...auth },
  });
  if (!res.ok) throw new Error(`DELETE ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

// The newer worker routes (onboarding onward) respond in the
// { data, error } envelope: success is { data, error: null }, failure
// is { data: null, error: { source, kind, message } } with a non-2xx
// status. Unlike apiPost, a non-2xx here is NOT thrown — the typed
// error object comes back to the caller, which renders kind-specific
// copy. Only a network failure or a non-JSON body throws.
export interface ApiEnvelopeError {
  source: "worker" | "msp";
  kind: string;
  message?: string;
}

export interface ApiEnvelope<T> {
  data: T | null;
  error: ApiEnvelopeError | null;
}

export async function apiPostEnvelope<T>(
  path: string,
  body: unknown,
): Promise<ApiEnvelope<T>> {
  const auth = await authHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: { "content-type": "application/json", ...auth },
    body: JSON.stringify(body),
  });
  const parsed = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
  if (!parsed || (parsed.data === undefined && parsed.error === undefined)) {
    // Not an envelope — a 429 from the rate limiter or a gateway
    // error page. Surface as a plain failure.
    throw new Error(`POST ${path} failed: ${res.status}`);
  }
  return parsed;
}

// Multipart upload — do NOT set Content-Type; the browser fills in
// the multipart boundary automatically.
export async function apiPostFormData<T>(path: string, form: FormData): Promise<T> {
  const auth = await authHeader();
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: { ...auth },
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`POST ${path} failed: ${res.status} ${text.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}
